import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import logger from '@ayocore/platform/common/helpers/logger';
import type { Folder } from '../../../schemas/folder.schema';
import { useFolderRepository } from '../../rpc';

/**
 * Потолок выборки папок. Диск грузится ОДНИМ запросом целиком, а не постранично:
 * дерево, крошки и расчёт прав зоны (подъём по `access_root_id`) требуют
 * предков, которых страница не гарантирует. 1000 папок с запасом покрывают
 * реальные инсталляции; если упрёмся — правильный ответ не «поднять лимит», а
 * серверная выдача поддерева.
 */
export const FOLDERS_LIMIT = 1000;

export interface UseDriveFoldersResult {
  /** Плоский список ВИДИМЫХ папок (сервер уже отфильтровал чужие private-зоны). */
  folders: Folder[];
  /** Те же папки по id — для путей, прав и цвета иконок. */
  foldersById: Map<number, Folder>;
  /** Корень Диска — единственная папка без родителя (COMPANY_DRIVE). */
  rootFolder: Folder | undefined;
  loading: boolean;
  error: string | null;
  /** Перечитать папки: дерево не видит чужих изменений до явного refresh. */
  reload: () => Promise<void>;
}

/**
 * useDriveFolders — загрузка папок Диска и производные от неё структуры.
 *
 * Единый источник для всех экранов Диска: страницы, встраиваемой панели,
 * файл-пикера и стенда. Раньше этот блок (запрос + `foldersById` + состояние
 * загрузки/ошибки) был скопирован в каждом из них.
 *
 * Папку-корень ОБЛАСТИ по системному якорю тут не ищем — это забота
 * `resolveRootByCode` из `driveScope`: экранам нужны разные корни (весь Диск
 * против папки сущности), а загрузка у них одна и та же.
 *
 * @example
 * ```tsx
 * const { folders, foldersById, rootFolder, loading, error, reload } = useDriveFolders();
 * ```
 */
export function useDriveFolders(): UseDriveFoldersResult {
  const folderRepo = useFolderRepository();

  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Живой ли компонент. Диск часто открыт в модалке (пикер, поиск), которую
   * закрывают, не дождавшись ответа — без этой проверки ответ прилетал бы в
   * состояние уже размонтированного компонента.
   */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    try {
      setError(null);
      // withMyAclLevel — мой ACL-уровень в custom-зонах (точный гейтинг кнопок)
      const data = await folderRepo.withMyAclLevel().limit(FOLDERS_LIMIT).fetch();
      if (mounted.current) setFolders(data ?? []);
    } catch (err) {
      logger.error('[useDriveFolders] Ошибка загрузки папок:', err);
      if (mounted.current) {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки Диска');
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
    // Прокси репозитория пересоздаётся на каждый рендер — в зависимости не включаем,
    // иначе reload менял бы идентичность и перезапускал эффект бесконечно.
     
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const foldersById = useMemo(() => new Map(folders.map(folder => [folder.id, folder])), [folders]);
  const rootFolder = useMemo(() => folders.find(folder => folder.parent_id == null), [folders]);

  return { folders, foldersById, rootFolder, loading, error, reload };
}
